/* global React, Icon */
const { useState: useStateTide, useEffect: useEffectTide, useMemo: useMemoTide } = React;

// ─── Config ────────────────────────────────────────────────────────────────────

// Single offshore Pacific reference point — swell is regional, not per-station
const PACIFIC_SWELL_POINT = { lat: 9.5, lon: -85.5 };

const STATIC_ASSET_VERSION = '20260516';
function versionedStaticAsset(path) {
  if (!path) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}v=${STATIC_ASSET_VERSION}`;
}

const TIDE_STATIONS = [
  { id: 'MA', label: 'Manuel Antonio',   lat:  9.3908, lon: -84.1711 },
  { id: 'PJ', label: 'Jacó Beach',       lat:  9.6167, lon: -84.6167 },
  { id: 'TA', label: 'Tamarindo',        lat: 10.2995, lon: -85.8409 },
  { id: 'CH', label: 'Playas del Coco',  lat: 10.5631, lon: -85.7047 },
  { id: 'DU', label: 'Costa Ballena',    lat:  9.1667, lon: -83.7333 },
  { id: 'HE', label: 'Herradura Beach',  lat:  9.6436, lon: -84.6797 },
  { id: 'SJ', label: 'San José',         lat:  9.9281, lon: -84.0907, hasTides: false, hasMarine: false },
  { id: 'FA', label: 'Arenal / La Fortuna', lat: 10.4678, lon: -84.6427, hasTides: false, hasMarine: false },
];

const TIDE_COPY = {
  en: {
    lang: 'EN',
    altLang: 'ES',
    kicker: 'CRMAPS Weather Planner',
    titleMain: 'Costa Rica Weather',
    titleAccent: '& Tide Chart',
    subtitle: 'Daily weather, weekly forecast, tide windows, daylight, moon phase and marine conditions for Costa Rica.',
    weather: 'Weather',
    tides: 'Tides',
    conditions: 'Conditions',
    sponsors: 'Sponsors',
    thisWeek: 'This Week',
    swellImpact: 'Swell Impact · Costa Rica',
    today: 'Today',
    feels: 'Feels',
    humidity: 'Humidity',
    rain: 'Rain',
    rainChance: 'Rain chance',
    unavailable: 'Weather unavailable',
    nextHigh: 'Next High',
    nextLow: 'Next Low',
    rising: 'Rising ↑',
    falling: 'Falling ↓',
    pacificCoast: 'Pacific coast',
    countryPlanner: 'Countrywide planner · UTC-6',
    noTides: 'Weather-only location',
    noTidesText: 'Tides and marine conditions are hidden for inland destinations.',
    swell: 'Swell',
    wind: 'Wind',
    water: 'Water',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    moon: 'Moon Phase',
    seaSurface: 'Sea surface',
    tonight: 'Tonight',
    days: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
    months: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
    weekDays: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'],
    weatherCodes: {
      0: ['Clear sky', 'SUN'], 1: ['Mainly clear', 'CLR'], 2: ['Partly cloudy', 'CLD'], 3: ['Overcast', 'OVC'],
      45: ['Fog', 'FOG'], 48: ['Rime fog', 'FOG'], 51: ['Light drizzle', 'DRZ'], 53: ['Drizzle', 'DRZ'],
      55: ['Dense drizzle', 'RAIN'], 61: ['Light rain', 'DRZ'], 63: ['Rain', 'RAIN'], 65: ['Heavy rain', 'RAIN'],
      80: ['Rain showers', 'DRZ'], 81: ['Showers', 'RAIN'], 82: ['Heavy showers', 'STORM'], 95: ['Thunderstorm', 'STORM'],
      96: ['Storm with hail', 'STORM'], 99: ['Storm with hail', 'STORM'],
    },
    moonLabels: {
      new: 'New Moon', waxingCrescent: 'Waxing Crescent', firstQuarter: 'First Quarter', waxingGibbous: 'Waxing Gibbous',
      full: 'Full Moon', waningGibbous: 'Waning Gibbous', thirdQuarter: 'Third Quarter', waningCrescent: 'Waning Crescent',
    },
  },
  es: {
    lang: 'ES',
    altLang: 'EN',
    kicker: 'Planificador del clima CRMAPS',
    titleMain: 'Clima de Costa Rica',
    titleAccent: 'y Tabla de Mareas',
    subtitle: 'Clima diario, pronostico semanal, ventanas de marea, luz solar, fase lunar y condiciones marinas para Costa Rica.',
    weather: 'Clima',
    tides: 'Mareas',
    conditions: 'Condiciones',
    sponsors: 'Patrocinadores',
    thisWeek: 'Esta Semana',
    swellImpact: 'Impacto del Oleaje · Costa Rica',
    today: 'Hoy',
    feels: 'Sensación',
    humidity: 'Humedad',
    rain: 'Lluvia',
    rainChance: 'Prob. lluvia',
    unavailable: 'Clima no disponible',
    nextHigh: 'Próxima Alta',
    nextLow: 'Próxima Baja',
    rising: 'Subiendo ↑',
    falling: 'Bajando ↓',
    pacificCoast: 'Costa Pacífica',
    countryPlanner: 'Planificador nacional · UTC-6',
    noTides: 'Ubicación solo clima',
    noTidesText: 'Las mareas y condiciones marinas se ocultan para destinos de interior.',
    swell: 'Oleaje',
    wind: 'Viento',
    water: 'Agua',
    sunrise: 'Amanecer',
    sunset: 'Atardecer',
    moon: 'Fase Lunar',
    seaSurface: 'Superficie del mar',
    tonight: 'Esta noche',
    days: ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'],
    months: ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'],
    weekDays: ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'],
    weatherCodes: {
      0: ['Despejado', 'SUN'], 1: ['Mayormente despejado', 'CLR'], 2: ['Parcialmente nublado', 'CLD'], 3: ['Nublado', 'OVC'],
      45: ['Neblina', 'FOG'], 48: ['Neblina con escarcha', 'FOG'], 51: ['Llovizna ligera', 'DRZ'], 53: ['Llovizna', 'DRZ'],
      55: ['Llovizna fuerte', 'RAIN'], 61: ['Lluvia ligera', 'DRZ'], 63: ['Lluvia', 'RAIN'], 65: ['Lluvia fuerte', 'RAIN'],
      80: ['Chubascos', 'DRZ'], 81: ['Aguaceros', 'RAIN'], 82: ['Aguaceros fuertes', 'STORM'], 95: ['Tormenta', 'STORM'],
      96: ['Tormenta con granizo', 'STORM'], 99: ['Tormenta con granizo', 'STORM'],
    },
    moonLabels: {
      new: 'Luna Nueva', waxingCrescent: 'Creciente', firstQuarter: 'Cuarto Creciente', waxingGibbous: 'Gibosa Creciente',
      full: 'Luna Llena', waningGibbous: 'Gibosa Menguante', thirdQuarter: 'Cuarto Menguante', waningCrescent: 'Menguante',
    },
  },
};

// ─── Swell impact reference (Pacific Costa Rica) ───────────────────────────────
const SWELL_RANGES = [
  { min: 315, max: 340, abbr: 'NNW', label: 'North-Northwest',
    desc: 'Northern hemisphere winter swell. Hits hard in the north and loses energy toward the south.',
    zones: 'Guanacaste · Jacó · Hermosa' },
  { min: 280, max: 310, abbr: 'WNW', label: 'West-Northwest',
    desc: 'Deep North Pacific swells or nearby tropical cyclones. Very direct entry.',
    zones: 'Guanacaste · Central Pacific · Boca Barranca' },
  { min: 240, max: 270, abbr: 'WSW', label: 'West-Southwest',
    desc: 'Transition swells or Central Pacific storms. Very consistent and powerful.',
    zones: 'All Pacific — Pavones · Dominical · Hermosa · Tamarindo' },
  { min: 210, max: 240, abbr: 'SW',  label: 'Southwest ★',
    desc: 'The king angle. South Pacific storms. Maximum power, consistency and size.',
    zones: 'All Pacific — Pavones · Hermosa · Santa Teresa · Avellanas' },
  { min: 180, max: 210, abbr: 'S',   label: 'South',
    desc: 'Classic South Pacific swell. Excellent for open beaches and south-facing point breaks.',
    zones: 'Pavones · Dominical · Playa Hermosa · Playa Grande' },
  { min: 160, max: 180, abbr: 'SSE', label: 'South-Southeast',
    desc: 'Narrow angle. The Osa and Nicoya Peninsulas block this swell in many beaches.',
    zones: 'Pavones · Matapalo · exposed southern beaches' },
];

// ─── Moon phase (pure astronomical calculation, no API) ───────────────────────
const KNOWN_NEW_MOON_MS = Date.UTC(2000, 0, 6, 0, 0, 0);
const LUNAR_CYCLE_DAYS  = 29.530589;
const MOON_PHASES = [
  { max:  1.85, key: 'new',            label: 'New Moon',        emoji: '🌑' },
  { max:  7.38, key: 'waxingCrescent', label: 'Waxing Crescent', emoji: '🌒' },
  { max:  9.22, key: 'firstQuarter',   label: 'First Quarter',   emoji: '🌓' },
  { max: 14.77, key: 'waxingGibbous',  label: 'Waxing Gibbous',  emoji: '🌔' },
  { max: 16.61, key: 'full',           label: 'Full Moon',       emoji: '🌕' },
  { max: 22.15, key: 'waningGibbous',  label: 'Waning Gibbous',  emoji: '🌖' },
  { max: 24.0,  key: 'thirdQuarter',   label: 'Third Quarter',   emoji: '🌗' },
  { max: 29.53, key: 'waningCrescent', label: 'Waning Crescent', emoji: '🌘' },
];
function getMoonPhase(ms) {
  const t         = (ms ?? Date.now()) - 6 * 3_600_000;
  const daysSince = (t - KNOWN_NEW_MOON_MS) / 86_400_000;
  const phase     = ((daysSince % LUNAR_CYCLE_DAYS) + LUNAR_CYCLE_DAYS) % LUNAR_CYCLE_DAYS;
  return MOON_PHASES.find(p => phase < p.max) || MOON_PHASES[0];
}

// ─── Time helpers (Costa Rica = UTC-6, no DST) ─────────────────────────────────
function crDayStartUnix() {
  const crMs = Date.now() - 6 * 3600_000;
  const crDayMs = Math.floor(crMs / 86_400_000) * 86_400_000;
  return Math.floor((crDayMs + 6 * 3600_000) / 1000);
}

function crHourDecimal() {
  const crMs = Date.now() - 6 * 3600_000;
  return (crMs % 86_400_000) / 3_600_000;
}

function tsToCRDecimal(unixTs) {
  const d = new Date(unixTs * 1000);
  return ((d.getUTCHours() - 6 + 24) % 24) + d.getUTCMinutes() / 60;
}

function tsToHHMM(unixTs) {
  const d = new Date(unixTs * 1000);
  const h = (d.getUTCHours() - 6 + 24) % 24;
  return `${String(h).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}`;
}

function timeUntil(unixTs, lang = 'en') {
  const mins = Math.round((unixTs - Date.now() / 1000) / 60);
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60), m = mins % 60;
  const prefix = lang === 'es' ? 'en' : 'in';
  if (h === 0) return `${prefix} ${m}m`;
  if (m === 0) return `${prefix} ${h}h`;
  return `${prefix} ${h}h ${m}m`;
}

function crDateDisplay(lang = 'en') {
  const d = new Date(Date.now() - 6 * 3600_000);
  const { days, months } = TIDE_COPY[lang] || TIDE_COPY.en;
  return `${days[d.getUTCDay()]}, ${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function compassDir(deg) {
  if (deg == null) return '';
  return ['N','NE','E','SE','S','SW','W','NW'][Math.round(deg / 45) % 8];
}

// ─── Tide fetcher (via server proxy with 24h server-side cache) ───────────────
async function fetchTides(station) {
  const dateStr = new Date(Date.now() - 6 * 3600_000).toISOString().slice(0, 10);
  const dayStart = crDayStartUnix();
  const dayEnd = dayStart + 86400;
  const ck      = `tide_v2_${station.id}_${dateStr}`;
  const cached  = sessionStorage.getItem(ck);
  if (cached) return JSON.parse(cached);

  const res = await fetch(`/api/tides/${station.id}?_=${dateStr}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const extremes = await res.json();

  const hasToday = Array.isArray(extremes)
    && extremes.some((entry) => Number(entry?.dt) >= dayStart && Number(entry?.dt) < dayEnd);
  if (!hasToday) return null;

  if (extremes.length > 0) {
    sessionStorage.setItem(ck, JSON.stringify(extremes));
  }
  return extremes;
}

async function fetchConditions(station) {
  const slot = Math.floor(Date.now() / 3600_000); // rotates every hour
  const ck = `cond_${station.id}_${slot}`;
  const cached = sessionStorage.getItem(ck);
  if (cached) return JSON.parse(cached);

  const needsMarine = station?.hasMarine !== false;
  const [marine, weather] = await Promise.all([
    needsMarine
      ? fetch(`https://marine-api.open-meteo.com/v1/marine?latitude=${PACIFIC_SWELL_POINT.lat}&longitude=${PACIFIC_SWELL_POINT.lon}&current=wave_height,wave_direction,wave_period,sea_surface_temperature&daily=wave_height_max,wave_direction_dominant&forecast_days=7&timezone=America%2FCosta_Rica`)
        .then(r => r.json()).catch(() => null)
      : Promise.resolve(null),
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${station.lat}&longitude=${station.lon}&current=temperature_2m,apparent_temperature,relative_humidity_2m,precipitation,weather_code,cloud_cover,wind_speed_10m,wind_direction_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset&timezone=America%2FCosta_Rica&forecast_days=7`)
      .then(r => r.json()).catch(() => null),
  ]);

  const result = { marine, weather };
  sessionStorage.setItem(ck, JSON.stringify(result));
  return result;
}

// ─── Curve from extremes via cosine interpolation ─────────────────────────────
function buildPtsFromExtremes(extremes) {
  if (!extremes || extremes.length < 2) return demoTodayPts();
  const start = crDayStartUnix();
  const sorted = [...extremes].sort((a, b) => a.dt - b.dt);
  const pts = [];
  for (let i = 0; i <= 48; i++) {
    const t     = i * 0.5;
    const unix  = start + t * 3600;
    const prev  = [...sorted].reverse().find(e => e.dt <= unix);
    const next  = sorted.find(e => e.dt > unix);
    let h;
    if (!prev)       h = next.height;
    else if (!next)  h = prev.height;
    else {
      const frac = (unix - prev.dt) / (next.dt - prev.dt);
      h = prev.height + (next.height - prev.height) * (1 - Math.cos(Math.PI * frac)) / 2;
    }
    pts.push({ t, h: +h.toFixed(3) });
  }
  return pts;
}

function buildTodayExtremes(extremes) {
  const start = crDayStartUnix();
  return (extremes || []).filter(e => e.dt >= start && e.dt < start + 86400);
}

function buildWeekData(extremes, swellDaily) {
  const start    = crDayStartUnix();
  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return Array.from({ length: 7 }, (_, i) => {
    const s = start + i * 86400, e = s + 86400;
    const events = (extremes || [])
      .filter(x => x.dt >= s && x.dt < e)
      .sort((a, b) => a.dt - b.dt)
      .map(x => ({ type: x.type, time: tsToHHMM(x.dt), height: x.height }));
    const d = new Date(s * 1000);
    const swellH   = swellDaily?.wave_height_max?.[i];
    const swellDir = swellDaily?.wave_direction_dominant?.[i];
    const swell = swellH != null ? { height: swellH, dir: swellDir } : null;
    const moon  = getMoonPhase(s * 1000);
    return { day: dayNames[d.getUTCDay()], date: String(d.getUTCDate()), events, today: i === 0, swell, moon };
  });
}

function getNextEvents(extremes) {
  const nowTs = Date.now() / 1000;
  const future = (extremes || []).filter(e => e.dt > nowTs).sort((a, b) => a.dt - b.dt);
  return {
    nextHigh: future.find(e => e.type === 'High') || null,
    nextLow:  future.find(e => e.type === 'Low')  || null,
  };
}

// ─── Demo fallback (no data) ────────────────────────────────────────────────
function demoH(t) {
  return +(1.05 + 0.92 * Math.sin((2 * Math.PI * (t - 3.1)) / 12.42)
           + 0.18 * Math.sin((2 * Math.PI * (t - 1.5)) / 6.21)).toFixed(2);
}
function demoTodayPts() {
  return Array.from({ length: 49 }, (_, i) => ({ t: i * 0.5, h: demoH(i * 0.5) }));
}

// ─── Interpolation helpers ─────────────────────────────────────────────────────
function interpolateH(pts, t) {
  if (!pts.length) return null;
  const prev = [...pts].reverse().find(p => p.t <= t);
  const next = pts.find(p => p.t > t);
  if (!prev) return next?.h ?? null;
  if (!next) return prev.h;
  const frac = (t - prev.t) / (next.t - prev.t);
  return prev.h + frac * (next.h - prev.h);
}

function tidePhase(pts, t) {
  const prev = [...pts].reverse().find(p => p.t <= t);
  const next = pts.find(p => p.t > t);
  if (!prev || !next) return '';
  return next.h > prev.h ? 'Rising' : 'Falling';
}

function hasTideData(station) {
  return station?.hasTides !== false;
}

function hasMarineData(station) {
  return station?.hasMarine !== false;
}

function weatherSummary(code, lang = 'en') {
  const copy = TIDE_COPY[lang] || TIDE_COPY.en;
  return copy.weatherCodes?.[Number(code)] || [lang === 'es' ? 'Clima' : 'Weather', 'WX'];
}

function weatherIconType(token) {
  if (token === 'SUN') return 'sun';
  if (token === 'CLR' || token === 'CLD') return 'partly';
  if (token === 'OVC') return 'cloud';
  if (token === 'FOG') return 'fog';
  if (token === 'DRZ' || token === 'RAIN') return 'rain';
  if (token === 'STORM') return 'storm';
  return 'partly';
}

function WeatherIcon({ token, small = false }) {
  const type = weatherIconType(token);
  return (
    <span className={`tc-weather-icon tc-weather-icon--${type} is-${type}${small ? ' tc-weather-icon--sm' : ''}`} aria-hidden="true">
      <span className="tc-weather-icon__sun"></span>
      <span className="tc-weather-icon__cloud"></span>
      <span className="tc-weather-icon__drop"></span>
      <span className="tc-weather-icon__drop"></span>
      <span className="tc-weather-icon__drop"></span>
      <span className="tc-weather-icon__bolt"></span>
      <span className="tc-weather-icon__fog"></span>
    </span>
  );
}

function moonLabel(moon, lang = 'en') {
  const copy = TIDE_COPY[lang] || TIDE_COPY.en;
  return copy.moonLabels?.[moon?.key] || moon?.label || '';
}

// ─── Sponsor slots: regionId + matchName resolve the real hotspot from prefetch ─
const TIDE_SPONSORS = [
  { regionId: 'DU', matchName: 'Costa Rica Dive & Surf', logo: versionedStaticAsset('/imagenes/sponsors/diveandsurf_sponsor_2.webp'), region: 'Costa Ballena · Osa' },
  { regionId: 'TA', clientId: '1721', matchName: 'COCO BEAUTY SPA', logo: versionedStaticAsset('/imagenes/sponsors/Coco_sponsor.webp'), region: 'Tamarindo' },
  { regionId: 'CH', clientId: '1709', matchName: 'COCONUTZ BREWHOUSE', logo: versionedStaticAsset('/imagenes/sponsors/coconutz_sponsor.webp'), region: 'Playas del Coco & Hermosa' },
  { regionId: 'PJ', clientId: '3504', matchName: 'SURF SYNERGY', logo: versionedStaticAsset('/imagenes/sponsors/surfsinergy_sponsor.webp'), region: 'Jacó Beach' },
];

async function resolveSponsorHotspot(sp) {
  const prefetch = window.CRMAPS_PREFETCH;
  if (!prefetch?.[sp.regionId]) return null;
  const hotspots = await Promise.resolve(prefetch[sp.regionId]);
  let hs = null;
  if (sp.clientId) {
    const clientId = String(sp.clientId);
    hs = hotspots.find(h => String(h.id || '').split('::dup::')[0] === clientId) || null;
  }
  if (!hs) {
    const needle = sp.matchName.toLowerCase();
    hs = hotspots.find(h => {
      const name = h.name?.toLowerCase() || '';
      return name === needle || name.includes(needle);
    }) || null;
  }
  return hs ? { ...hs, _mapaId: sp.regionId } : null;
}

function tideTrack(data) {
  try {
    let uid = localStorage.getItem('crmaps_uid');
    if (!uid) { uid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`; localStorage.setItem('crmaps_uid', uid); }
    let sid = sessionStorage.getItem('crmaps_sid');
    if (!sid) { sid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`; sessionStorage.setItem('crmaps_sid', sid); }
    const attribution = typeof window.CRMapsAnalyticsContext === 'function' ? window.CRMapsAnalyticsContext() : {};
    const payload = JSON.stringify({ ...data, ...attribution, userId: uid, sessionId: sid, ts: new Date().toISOString() });
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/analytics/track', new Blob([payload], { type: 'application/json' }));
    } else {
      fetch('/api/analytics/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
    }
  } catch {}
}

// ─── Component ─────────────────────────────────────────────────────────────────
function TideScreen({ onCardClick, lang: appLang, onLanguageChange }) {
  const [stIdx, setStIdx]       = useStateTide(0);
  const [extremes, setExtremes] = useStateTide(null);
  const [cond, setCond]         = useStateTide(null);
  const [loading, setLoading]   = useStateTide(true);
  const [dropOpen, setDropOpen] = useStateTide(false);
  const [localLang, setLocalLang] = useStateTide(() => {
    try {
      const saved = localStorage.getItem('crmaps-lang');
      if (saved === 'es' || saved === 'en') return saved;
    } catch {}
    return (navigator.language || '').toLowerCase().startsWith('es') ? 'es' : 'en';
  });
  const lang = appLang || localLang;
  const setLang = onLanguageChange || setLocalLang;

  const station = TIDE_STATIONS[stIdx];
  const copy = TIDE_COPY[lang] || TIDE_COPY.en;
  const showTides = hasTideData(station);
  const showMarine = hasMarineData(station);

  useEffectTide(() => {
    let live = true;
    setLoading(true);
    setExtremes(null);
    const tidePromise = showTides ? fetchTides(station) : Promise.resolve(null);
    Promise.all([tidePromise, fetchConditions(station)])
      .then(([ex, co]) => {
        if (!live) return;
        setExtremes(ex);
        setCond(co);
        setLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setLoading(false);
      });
    return () => { live = false; };
  }, [stIdx, showTides]); // eslint-disable-line

  useEffectTide(() => {
    try { localStorage.setItem('crmaps-lang', lang); } catch {}
  }, [lang]);

  // ── Derived data ──────────────────────────────────────────────────────────────
  const nowT    = crHourDecimal();
  const nowHHMM = `${String(Math.floor(nowT)).padStart(2,'0')}:${String(Math.floor((nowT%1)*60)).padStart(2,'0')}`;

  const todayPts      = useMemoTide(() => buildPtsFromExtremes(extremes),     [extremes]);
  const todayExtremes = useMemoTide(() => buildTodayExtremes(extremes),        [extremes]);
  const weekData      = useMemoTide(() => buildWeekData(extremes, cond?.marine?.daily), [extremes, cond]);
  const { nextHigh, nextLow } = useMemoTide(() => getNextEvents(extremes),    [extremes]);
  const nowH  = useMemoTide(() => interpolateH(todayPts, nowT), [todayPts]);
  const phase = useMemoTide(() => tidePhase(todayPts, nowT),    [todayPts]);
  const weatherCurrent = cond?.weather?.current || {};
  const weatherDaily = cond?.weather?.daily || {};
  const [todaySummary, todayIcon] = weatherSummary(weatherCurrent.weather_code, lang);

  // ── SVG ───────────────────────────────────────────────────────────────────────
  const W = 360, H = 160, padL = 36, padR = 12, padT = 12, padB = 34;
  const allH = todayPts.map(p => p.h);
  const minH = -2 / 3.281;
  const maxH = allH.length ? Math.max(...allH, 2.0) * 1.15 : 2.5;
  const xs   = t => padL + (t / 24) * (W - padL - padR);
  const ys   = h => padT + (1 - (h - minH) / (maxH - minH)) * (H - padT - padB);

  let svgPath = '';
  if (todayPts.length >= 2) {
    svgPath = `M ${xs(todayPts[0].t)} ${ys(todayPts[0].h)}`;
    for (let i = 1; i < todayPts.length; i++) {
      const cx = xs((todayPts[i-1].t + todayPts[i].t) / 2);
      svgPath += ` Q ${xs(todayPts[i-1].t)} ${ys(todayPts[i-1].h)}, ${cx} ${(ys(todayPts[i-1].h)+ys(todayPts[i].h))/2}`;
      svgPath += ` T ${xs(todayPts[i].t)} ${ys(todayPts[i].h)}`;
    }
  }
  const areaPath = svgPath ? `${svgPath} L ${xs(24)} ${H-padB} L ${padL} ${H-padB} Z` : '';
  const nowX = xs(nowT);
  const nowY = nowH != null ? ys(nowH) : H / 2;

  // Y-axis ticks in ft (keep internal math in meters)
  const mTicks = [minH, 0, 0.5, 1.0, 1.5, 2.0, 2.5].filter(v => v <= maxH * 1.05);

  // ── Conditions ────────────────────────────────────────────────────────────────
  const waveH   = cond?.marine?.current?.wave_height;
  const waveDir = cond?.marine?.current?.wave_direction;
  const wavePer = cond?.marine?.current?.wave_period;
  const waterT  = cond?.marine?.current?.sea_surface_temperature;
  const windSpd = cond?.weather?.current?.wind_speed_10m;
  const windDir = cond?.weather?.current?.wind_direction_10m;
  const sunrise = cond?.weather?.daily?.sunrise?.[0]?.split('T')[1]?.slice(0, 5);
  const sunset  = cond?.weather?.daily?.sunset?.[0]?.split('T')[1]?.slice(0, 5);
  const moon = getMoonPhase();
  const phaseText = phase === 'Rising' ? copy.rising : phase === 'Falling' ? copy.falling : '';
  const temp = Number.isFinite(Number(weatherCurrent.temperature_2m)) ? `${Math.round(Number(weatherCurrent.temperature_2m))}°C` : '—';
  const feels = Number.isFinite(Number(weatherCurrent.apparent_temperature)) ? `${Math.round(Number(weatherCurrent.apparent_temperature))}°C` : '—';
  const humidity = Number.isFinite(Number(weatherCurrent.relative_humidity_2m)) ? `${Math.round(Number(weatherCurrent.relative_humidity_2m))}%` : '—';
  const rain = Number.isFinite(Number(weatherCurrent.precipitation)) ? `${Number(weatherCurrent.precipitation).toFixed(1)} mm` : '—';

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="tide-screen tide-screen--planner">

      {/* ── Hero ── */}
      <div className="tide-hero">
        <div className="app-lang-toggle app-lang-toggle--hero" aria-label="Language selector">
          <button type="button" className={lang === 'es' ? 'is-on' : ''} onClick={() => setLang('es')} aria-pressed={lang === 'es'}>ES</button>
          <button type="button" className={lang === 'en' ? 'is-on' : ''} onClick={() => setLang('en')} aria-pressed={lang === 'en'}>EN</button>
        </div>
        <div className="tide-hero-controls">
          <div className="tide-station-wrap">
            <button
              className="tide-loc tide-loc-btn"
              onClick={() => setDropOpen(o => !o)}
            >
              {Icon.pin}
              <span>{station.label}</span>
              <span className={'ms-chevron' + (dropOpen ? ' is-open' : '')} style={{ marginLeft: 4 }}>{Icon.chevDown}</span>
            </button>

            {dropOpen && (
              <div className="map-dropdown tide-station-dropdown">
                {TIDE_STATIONS.map((s, i) => (
                  <button key={s.id} className={'map-dropdown-item' + (i === stIdx ? ' is-selected' : '')}
                    onClick={() => { setStIdx(i); setDropOpen(false); }}>
                    <span>{s.label}</span>
                    {i === stIdx && <span className="mdi-check">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="tide-date">{crDateDisplay(lang)}</div>
        </div>

        <div className="tc-weather-today">
          <div className="tc-weather-today__icon"><WeatherIcon token={todayIcon} /></div>
          <div>
            <div className="tc-weather-today__temp">{loading ? '—' : temp}</div>
            <div className="tc-weather-today__label">{copy.today} · {todaySummary}</div>
            <div className="tc-weather-today__meta">{station.label} · {copy.feels} {feels} · {copy.humidity} {humidity} · {copy.rain} {rain}</div>
          </div>
        </div>
      </div>

      <section className="tide-weather-section">
        <h4>{copy.weather}</h4>
        <div className="tc-weather-week">
          {loading && !weatherDaily.time ? Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="tc-weather-day pulse">
              <div className="skeleton" style={{ height: 12, width: 42 }}></div>
              <div className="skeleton" style={{ height: 26, width: 38 }}></div>
              <div className="skeleton" style={{ height: 18, width: 64 }}></div>
            </div>
          )) : weatherDaily.time?.length ? weatherDaily.time.slice(0, 7).map((dateStr, index) => {
            const date = new Date(`${dateStr}T12:00:00-06:00`);
            const [summary, icon] = weatherSummary(weatherDaily.weather_code?.[index], lang);
            const max = Number.isFinite(Number(weatherDaily.temperature_2m_max?.[index])) ? `${Math.round(Number(weatherDaily.temperature_2m_max[index]))}°` : '—';
            const min = Number.isFinite(Number(weatherDaily.temperature_2m_min?.[index])) ? `${Math.round(Number(weatherDaily.temperature_2m_min[index]))}°` : '—';
            const rainChance = Number.isFinite(Number(weatherDaily.precipitation_probability_max?.[index])) ? `${Math.round(Number(weatherDaily.precipitation_probability_max[index]))}%` : '—';
            return (
              <div key={dateStr} className={`tc-weather-day${index === 0 ? ' today' : ''}`}>
                <div className="tc-weather-day__top">
                  <div className="tc-weather-day__name">{copy.weekDays[date.getDay()]}</div>
                  <div className="tc-weather-day__temp">{max} / {min}</div>
                </div>
                <div className="tc-weather-day__icon"><WeatherIcon token={icon} small /></div>
                <div className="tc-weather-day__detail">
                  <div className="tc-weather-day__label">{summary}</div>
                  <div className="tc-weather-day__rain">{copy.rainChance}: {rainChance}</div>
                </div>
              </div>
            );
          }) : (
            <div className="tc-weather-day"><div className="tc-weather-day__label">{copy.unavailable}</div></div>
          )}
        </div>
      </section>

      {!showTides && (
        <div className="tide-no-tides">
          <strong>{copy.noTides}</strong>
          <span>{copy.noTidesText}</span>
        </div>
      )}

      {showTides && (
      <section className="tide-tides-section">
        <h4>{copy.tides}</h4>
        <div className="tide-stat-row">
          {loading ? (
            [0,1].map(k => (
              <div key={k} className="tide-stat" style={{ opacity: 0.45 }}>
                <div className="tide-stat-label">{k === 0 ? `↑ ${copy.nextHigh}` : `↓ ${copy.nextLow}`}</div>
                <div className="tide-stat-time">--:--</div>
                <div className="tide-stat-height">Loading…</div>
              </div>
            ))
          ) : (
            <>
              {nextHigh && (
                <div className="tide-stat">
                  <div className="tide-stat-label">↑ {copy.nextHigh}</div>
                  <div className="tide-stat-time">{tsToHHMM(nextHigh.dt)}</div>
                  <div className="tide-stat-height">{(nextHigh.height * 3.281).toFixed(1)} ft · {timeUntil(nextHigh.dt, lang)}</div>
                  <div className="arrow" style={{ background: 'rgba(242,201,76,0.3)' }}>
                    <svg viewBox="0 0 12 12" fill="#f2c94c"><path d="m6 1 5 6H7v4H5V7H1z"/></svg>
                  </div>
                </div>
              )}
              {nextLow && (
                <div className="tide-stat">
                  <div className="tide-stat-label">↓ {copy.nextLow}</div>
                  <div className="tide-stat-time">{tsToHHMM(nextLow.dt)}</div>
                  <div className="tide-stat-height">{(nextLow.height * 3.281).toFixed(1)} ft · {timeUntil(nextLow.dt, lang)}</div>
                  <div className="arrow">
                    <svg viewBox="0 0 12 12" fill="#fff"><path d="m6 11 5-6H7V1H5v4H1z"/></svg>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="tide-graph-card">
        <div className="tide-graph-h">
          <div className="now">
            {nowH != null ? (nowH * 3.281).toFixed(1) : '--'}
            <small>ft · {nowHHMM}</small>
          </div>
          <div className="label">{phaseText}</div>
        </div>
        <svg className="tide-svg" viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <linearGradient id="tideFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0061b4" stopOpacity="0.34"/>
              <stop offset="100%" stopColor="#0061b4" stopOpacity="0.02"/>
            </linearGradient>
          </defs>

          {mTicks.map(v => {
            const y = ys(v);
            return (
              <g key={v}>
                <line x1={padL} x2={W-padR} y1={y} y2={y} stroke="rgba(48,65,72,0.08)" strokeDasharray="2 3"/>
                <text x={padL-4} y={y+3} fontSize="9" fill="var(--fg-5)" textAnchor="end" fontWeight="600">
                  {Math.round(v * 3.281)}ft
                </text>
              </g>
            );
          })}
          {[0, 6, 12, 18, 24].map(h => {
            const x = xs(h);
            return (
              <g key={h}>
                <line x1={x} x2={x} y1={padT} y2={H-padB} stroke="rgba(48,65,72,0.06)"/>
                <text x={x} y={H-8} fontSize="9" fill="var(--fg-5)" textAnchor="middle" fontWeight="700" letterSpacing="0.06em">
                  {String(h).padStart(2,'0')}h
                </text>
              </g>
            );
          })}

          {areaPath && <path d={areaPath} fill="url(#tideFill)"/>}
          {svgPath  && <path d={svgPath}  fill="none" stroke="#0061b4" strokeWidth="2.5" strokeLinecap="round"/>}

          {todayExtremes.map((e, i) => (
            <g key={i}>
              <circle cx={xs(tsToCRDecimal(e.dt))} cy={ys(e.height)} r="4"
                fill={e.type === 'High' ? '#f2c94c' : '#0061b4'} stroke="#fff" strokeWidth="2"/>
              <text
                x={xs(tsToCRDecimal(e.dt))}
                y={e.type === 'Low' ? ys(e.height) + 18 : ys(e.height) - 8}
                fontSize="9"
                fill="var(--fg-4)"
                textAnchor="middle"
                fontWeight="800"
              >
                {(e.height * 3.281).toFixed(1)}ft
              </text>
            </g>
          ))}

          <line x1={nowX} x2={nowX} y1={padT} y2={H-padB} stroke="#f97316" strokeWidth="1.5" strokeDasharray="3 2"/>
          <circle cx={nowX} cy={nowY} r="5" fill="#f97316" stroke="#fff" strokeWidth="2.5"/>
        </svg>
      </div>

      {showMarine && (
      <>
      {/* ── Marine conditions ── */}
      <div className="tide-section">
        <h4>{copy.conditions}</h4>
        <div className="tide-cond-grid">
          <div className="tide-cond">
            <div className="c-val">{waveH != null ? `${(waveH*3.281).toFixed(1)} ft` : '—'}</div>
            <div className="c-label">{copy.swell}</div>
            <div className="c-sub">{[waveDir != null ? `${compassDir(waveDir)} ${Math.round(waveDir)}°` : '', wavePer != null ? `${Math.round(wavePer)}s` : ''].filter(Boolean).join(' · ') || '—'}</div>
          </div>
          <div className="tide-cond">
            <div className="c-val">{windSpd != null ? `${Math.round(windSpd)} km/h` : '—'}</div>
            <div className="c-label">{copy.wind}</div>
            <div className="c-sub">{compassDir(windDir) || '—'}</div>
          </div>
          <div className="tide-cond">
            <div className="c-val">{waterT != null ? `${Math.round(waterT)}°C` : '—'}</div>
            <div className="c-label">{copy.water}</div>
            <div className="c-sub">{copy.seaSurface}</div>
          </div>
          <div className="tide-cond">
            <div className="c-val">{sunrise || '—'}</div>
            <div className="c-label">{copy.sunrise}</div>
            <div className="c-sub">{copy.pacificCoast}</div>
          </div>
          <div className="tide-cond">
            <div className="c-val">{sunset || '—'}</div>
            <div className="c-label">{copy.sunset}</div>
            <div className="c-sub">{copy.pacificCoast}</div>
          </div>
          <div className="tide-cond">
            <div className="c-val">{moon.emoji} {moonLabel(moon, lang)}</div>
            <div className="c-label">{copy.moon}</div>
            <div className="c-sub">{copy.tonight}</div>
          </div>
        </div>
      </div>
      </>
      )}

      {/* ── Sponsors ── */}
      <div className="tide-sponsors-wrap">
        <h4 style={{ font: '800 11px/1 var(--font-sans)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-5)', margin: '0 0 10px' }}>{copy.sponsors}</h4>
        <div className="tide-sponsors">
          {TIDE_SPONSORS.filter(Boolean).map((sp, i) => (
            <button key={i} className="sponsor-slot sponsor-slot--active" onClick={async () => {
              if (!onCardClick) return;
              const hs = await resolveSponsorHotspot(sp);
              if (!hs) return;
              tideTrack({ type: 'tide_sponsor_click', sponsor: sp.matchName, mapCode: sp.regionId });
              onCardClick(hs);
            }}>
              <img src={sp.logo} alt={sp.matchName} className="sponsor-logo-img" />
            </button>
          ))}
        </div>
      </div>

      {/* ── Week rail ── */}
      <div style={{ padding: '20px 16px 0' }}>
        <h4 style={{ font: '800 11px/1 var(--font-sans)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-5)', margin: '0 0 10px' }}>{copy.thisWeek}</h4>
      </div>
      <div className="tide-week-rail">
        {!loading ? weekData.map((d, i) => (
          <div key={i} className={'tide-day' + (d.today ? ' is-today' : '')}>
            <div className="d-day">{copy.weekDays[(new Date((crDayStartUnix() + i * 86400) * 1000)).getUTCDay()] || d.day}</div>
            <div className="d-date">{d.date}</div>
            {d.events.length ? d.events.map((e, j) => (
              <div key={j} className={e.type === 'High' ? 'd-hi' : 'd-lo'}>
                <span className="d-arrow">{e.type === 'High' ? '↑' : '↓'}</span>
                <span className="d-time">{e.time}</span>
                <span className="d-ht">{(e.height * 3.281).toFixed(1)}ft</span>
              </div>
            )) : <div className="d-lo" style={{ opacity: 0.4 }}>—</div>}
            {d.swell && (
              <div className="d-swell">
                <span>{(d.swell.height * 3.281).toFixed(1)} ft</span>
                <span>{d.swell.dir != null ? `${compassDir(d.swell.dir)} ${Math.round(d.swell.dir)}°` : ''}</span>
              </div>
            )}
            <div className="d-moon"><span>{d.moon.emoji}</span><span>{moonLabel(d.moon, lang)}</span></div>
          </div>
        )) : loading ? (
          Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="tide-day" style={{ opacity: 0.35 }}>
              <div className="d-day">---</div><div className="d-date">--</div>
              <div className="d-hi">—</div><div className="d-lo">—</div>
            </div>
          ))
        ) : null}
      </div>

      {/* ── Swell impact reference ── */}
      <div className="tide-section">
        <h4>{copy.swellImpact}</h4>
        <div className="swell-impact-list">
          {SWELL_RANGES.map(r => {
            const active = waveDir != null && waveDir >= r.min && waveDir <= r.max;
            return (
              <div key={r.abbr} className={`si-row${active ? ' is-active' : ''}`}>
                <div className="si-left">
                  <span className="si-abbr">{r.abbr}</span>
                  <span className="si-range">{r.min}°–{r.max}°</span>
                </div>
                <div className="si-right">
                  <div className="si-label">{r.label}</div>
                  <div className="si-desc">{r.desc}</div>
                  <div className="si-zones">{r.zones}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </section>
      )}

    </div>
  );
}

Object.assign(window, { TideScreen });
